import { Avatar, AvatarFallback, Badge, Button, Card, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Trash2, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import {
  addGroupMember,
  getGroup,
  listGroupMembers,
  removeGroupMember,
  updateGroupMember,
  type GroupMember,
  type GroupMemberRole,
  type GroupMemberStatus,
  type GroupVisibility,
} from '../../lib/teams-groups-api'

const visibilityVariant: Record<GroupVisibility, 'success' | 'gold' | 'neutral'> = {
  public: 'success',
  unlisted: 'gold',
  private: 'neutral',
}

const roleLabel: Record<GroupMemberRole, string> = {
  member: 'Member',
  leader: 'Leader',
  co_leader: 'Co-leader',
  host: 'Host',
}

const statusVariant: Record<GroupMemberStatus, 'success' | 'gold' | 'neutral' | 'warning'> = {
  active: 'success',
  pending: 'gold',
  inactive: 'neutral',
  archived: 'warning',
}

const ROLES: GroupMemberRole[] = ['member', 'leader', 'co_leader', 'host']
const STATUSES: GroupMemberStatus[] = ['active', 'pending', 'inactive', 'archived']

function initials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean)
  return (
    `${parts[0]?.[0] ?? ''}${parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''}`.toUpperCase() ||
    '·'
  )
}

export function GroupDetail() {
  const { id = '' } = useParams()
  const queryClient = useQueryClient()
  const [personId, setPersonId] = useState('')
  const [addRole, setAddRole] = useState<GroupMemberRole>('member')

  const groupQuery = useQuery({
    queryKey: ['group', id],
    queryFn: () => getGroup(id),
    enabled: !!id,
  })
  const membersQuery = useQuery({
    queryKey: ['group', id, 'members'],
    queryFn: () => listGroupMembers(id),
    enabled: !!id,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['group', id] })
    queryClient.invalidateQueries({ queryKey: ['groups'] })
  }

  const addMutation = useMutation({
    mutationFn: () => addGroupMember(id, { personId: personId.trim(), role: addRole }),
    onSuccess: () => {
      setPersonId('')
      setAddRole('member')
      invalidate()
    },
  })

  const updateMutation = useMutation({
    mutationFn: (input: { memberId: string; role?: GroupMemberRole; status?: GroupMemberStatus }) =>
      updateGroupMember(id, input.memberId, { role: input.role, status: input.status }),
    onSuccess: invalidate,
  })

  const removeMutation = useMutation({
    mutationFn: (memberId: string) => removeGroupMember(id, memberId),
    onSuccess: invalidate,
  })

  const group = groupQuery.data

  return (
    <div className="space-y-6">
      <Link
        to="/app/groups"
        className="inline-flex items-center gap-1.5 text-sm text-muted transition-colors hover:text-fg"
      >
        <ArrowLeft className="size-4" /> Back to groups
      </Link>

      {groupQuery.isLoading ? (
        <Card>
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading group…
          </div>
        </Card>
      ) : groupQuery.isError || !group ? (
        <Card>
          <div className="py-20 text-center text-sm text-danger">
            {groupQuery.error instanceof Error ? groupQuery.error.message : 'Group not found.'}
          </div>
        </Card>
      ) : (
        <>
          <PageHeader
            title={group.name}
            description={group.teamName ? `Part of ${group.teamName}` : undefined}
            actions={
              <>
                <Badge variant={visibilityVariant[group.visibility]} className="capitalize">
                  {group.visibility}
                </Badge>
                <Badge variant="neutral" className="capitalize">
                  {group.enrollment === 'request' ? 'request to join' : group.enrollment}
                </Badge>
              </>
            }
          />

          {group.description && (
            <Card>
              <div className="p-5 text-sm text-muted">{group.description}</div>
            </Card>
          )}

          <Card className="overflow-hidden p-0">
            <div className="flex flex-col gap-3 border-b border-border p-5 sm:flex-row sm:items-end">
              <div className="flex-1 space-y-1.5">
                <label htmlFor="add-person" className="text-xs font-semibold text-muted">
                  Add member (person ID)
                </label>
                <input
                  id="add-person"
                  value={personId}
                  onChange={(e) => setPersonId(e.target.value)}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-fg placeholder:text-dim focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="add-role" className="text-xs font-semibold text-muted">
                  Role
                </label>
                <select
                  id="add-role"
                  value={addRole}
                  onChange={(e) => setAddRole(e.target.value as GroupMemberRole)}
                  className="h-9 rounded-lg border border-border bg-card px-3 text-sm text-fg focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {roleLabel[r]}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                onClick={() => addMutation.mutate()}
                disabled={!personId.trim() || addMutation.isPending}
              >
                <UserPlus /> {addMutation.isPending ? 'Adding…' : 'Add'}
              </Button>
            </div>

            {addMutation.isError && (
              <p className="px-5 pt-3 text-sm text-danger">
                {addMutation.error instanceof Error
                  ? addMutation.error.message
                  : 'Could not add member.'}
              </p>
            )}

            {membersQuery.isLoading ? (
              <div className="flex items-center justify-center gap-2 py-16 text-muted">
                <Spinner /> Loading members…
              </div>
            ) : membersQuery.isError ? (
              <div className="py-16 text-center text-sm text-danger">Couldn’t load members.</div>
            ) : !membersQuery.data || membersQuery.data.length === 0 ? (
              <div className="py-16 text-center text-sm text-muted">
                No members yet. Add the first person above.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                      <th className="px-5 py-3 font-semibold">Name</th>
                      <th className="px-5 py-3 font-semibold">Role</th>
                      <th className="px-5 py-3 font-semibold">Status</th>
                      <th className="px-5 py-3 font-semibold sr-only">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {membersQuery.data.map((m) => (
                      <MemberRow
                        key={m.id}
                        member={m}
                        onRole={(role) => updateMutation.mutate({ memberId: m.id, role })}
                        onStatus={(status) => updateMutation.mutate({ memberId: m.id, status })}
                        onRemove={() => removeMutation.mutate(m.id)}
                        busy={updateMutation.isPending || removeMutation.isPending}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  )
}

function MemberRow({
  member,
  onRole,
  onStatus,
  onRemove,
  busy,
}: {
  member: GroupMember
  onRole: (role: GroupMemberRole) => void
  onStatus: (status: GroupMemberStatus) => void
  onRemove: () => void
  busy: boolean
}) {
  return (
    <tr className="border-b border-border-soft transition-colors last:border-0 hover:bg-card-hover">
      <td className="px-5 py-3">
        <div className="flex items-center gap-3">
          <Avatar className="size-8">
            <AvatarFallback>{initials(member.personName)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="font-medium">{member.personName}</div>
            {member.personEmail && (
              <div className="truncate text-xs text-dim">{member.personEmail}</div>
            )}
          </div>
        </div>
      </td>
      <td className="px-5 py-3">
        <select
          value={member.role}
          disabled={busy}
          onChange={(e) => onRole(e.target.value as GroupMemberRole)}
          aria-label={`Role for ${member.personName}`}
          className="h-8 rounded-md border border-border bg-card px-2 text-xs text-fg focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {roleLabel[r]}
            </option>
          ))}
        </select>
      </td>
      <td className="px-5 py-3">
        <div className="flex items-center gap-2">
          <Badge variant={statusVariant[member.status]} className="capitalize">
            {member.status}
          </Badge>
          <select
            value={member.status}
            disabled={busy}
            onChange={(e) => onStatus(e.target.value as GroupMemberStatus)}
            aria-label={`Status for ${member.personName}`}
            className="h-8 rounded-md border border-border bg-card px-2 text-xs text-fg focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </td>
      <td className="px-5 py-3 text-right">
        <Button
          variant="ghost"
          size="icon-sm"
          disabled={busy}
          aria-label={`Remove ${member.personName}`}
          onClick={onRemove}
        >
          <Trash2 className="text-danger" />
        </Button>
      </td>
    </tr>
  )
}
